import React, { useState, useEffect, useCallback } from 'react';

const BASE         = 'https://fotileams.com/api';
const CRED_KEY     = 'sa_fp_credential';
const FP_EN_KEY    = 'sa_fp_enabled';
const SESSION_KEY  = 'sa_loc_session';
const FALLBACK_PIN = '1971123';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('super_token')}`,
  };
}

async function sha256(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,'0')).join('');
}

function isFpEnabled() {
  const v = localStorage.getItem(FP_EN_KEY);
  return v === null ? true : v === 'true';
}

function isSessionOk() {
  return sessionStorage.getItem(SESSION_KEY) === '1';
}

function markSession() {
  sessionStorage.setItem(SESSION_KEY, '1');
}

function getStoredCred() {
  try {
    const raw = localStorage.getItem(CRED_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// ── FINGERPRINT GATE ──────────────────────────────────────
function FingerprintGate({ onVerified }) {
  const [step, setStep]       = useState('init'); // init|register|scan|pin|registering|scanning
  const [pin, setPin]         = useState('');
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);
  const fpEnabled             = isFpEnabled();
  const hasCred               = !!getStoredCred();

  useEffect(() => {
    if (!fpEnabled) {
      setStep('pin');
    } else if (!hasCred) {
      setStep('register');
    } else {
      setStep('scan');
    }
  }, []);

  // ── REGISTER ──
  const doRegister = async () => {
    setBusy(true); setError('');
    setStep('registering');
    try {
      const challenge = crypto.getRandomValues(new Uint8Array(32));
      const userId    = crypto.getRandomValues(new Uint8Array(16));

      const cred = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp:   { name: 'Fotile AMS Super Admin', id: window.location.hostname },
          user: { id: userId, name: 'BOSS-001', displayName: 'Super Admin' },
          pubKeyCredParams: [
            { alg: -7,   type: 'public-key' }, // ES256
            { alg: -257, type: 'public-key' }, // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform', // device biometric only
            requireResidentKey:      false,
            userVerification:        'required', // must verify biometric
          },
          timeout: 60000,
          attestation: 'none',
        }
      });

      if (cred) {
        // Store credential ID so we can verify against this specific registration
        const credData = {
          id:      cred.id,
          rawId:   Array.from(new Uint8Array(cred.rawId)),
          type:    cred.type,
        };
        localStorage.setItem(CRED_KEY, JSON.stringify(credData));
        markSession();
        onVerified();
      }
    } catch (e) {
      console.error(e);
      if (e.name === 'NotAllowedError') {
        setError('Fingerprint cancelled or not allowed. Use PIN instead.');
      } else if (e.name === 'NotSupportedError') {
        setError('Fingerprint not supported on this device. Use PIN.');
      } else {
        setError('Registration failed. Use PIN instead.');
      }
      setStep('pin');
    } finally {
      setBusy(false);
    }
  };

  // ── VERIFY ──
  const doVerify = async () => {
    setBusy(true); setError('');
    setStep('scanning');
    try {
      const stored    = getStoredCred();
      if (!stored) { setStep('register'); setBusy(false); return; }

      const challenge = crypto.getRandomValues(new Uint8Array(32));

      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge,
          allowCredentials: [{
            type: 'public-key',
            id:   new Uint8Array(stored.rawId),
          }],
          userVerification: 'required', // must verify biometric
          timeout: 30000,
        }
      });

      if (assertion) {
        markSession();
        onVerified();
      }
    } catch (e) {
      console.error(e);
      if (e.name === 'NotAllowedError') {
        setError('Fingerprint not recognised or cancelled. Use PIN.');
      } else {
        setError('Verification failed. Use PIN instead.');
      }
      setStep('pin');
    } finally {
      setBusy(false);
    }
  };

  // ── PIN ──
  const doPin = async () => {
    if (!pin) return;
    setBusy(true); setError('');
    try {
      const entered  = await sha256(pin);
      const correct  = await sha256(FALLBACK_PIN);
      if (entered === correct) {
        markSession();
        onVerified();
      } else {
        setError('Incorrect PIN');
        setPin('');
      }
    } finally { setBusy(false); }
  };

  const SCREENS = {
    register: {
      icon: '✋',
      title: 'Register Your Fingerprint',
      sub: 'First-time setup. Your fingerprint will be linked to this device. Only your registered fingerprint will work.',
      actions: (
        <>
          <button className="fp-btn fp-btn-primary" onClick={doRegister} disabled={busy}>
            👆 Register Fingerprint Now
          </button>
          <button className="fp-btn fp-btn-secondary" onClick={() => setStep('pin')}>
            Skip — Use PIN instead
          </button>
        </>
      )
    },
    registering: {
      icon: '✋',
      title: 'Place Your Finger...',
      sub: 'Follow the prompt on your screen to complete fingerprint registration.',
      actions: <div className="fp-scanning-indicator"><span/><span/><span/></div>
    },
    scan: {
      icon: '👆',
      title: 'Verify Your Identity',
      sub: 'Locations tab is protected. Use your registered fingerprint to continue.',
      actions: (
        <>
          <button className="fp-btn fp-btn-primary" onClick={doVerify} disabled={busy}>
            {busy ? 'Scanning…' : '👆 Scan Fingerprint'}
          </button>
          <button className="fp-btn fp-btn-secondary" onClick={() => setStep('pin')}>
            Use PIN instead
          </button>
          <button className="fp-btn fp-btn-link" onClick={() => {
            localStorage.removeItem(CRED_KEY);
            setStep('register');
          }}>
            Re-register fingerprint
          </button>
        </>
      )
    },
    scanning: {
      icon: '👆',
      title: 'Scanning...',
      sub: 'Place your registered finger on the sensor.',
      actions: <div className="fp-scanning-indicator"><span/><span/><span/></div>
    },
    pin: {
      icon: '🔢',
      title: 'Enter Access PIN',
      sub: 'Enter your Super Admin PIN to access Locations.',
      actions: (
        <>
          <input className="fp-pin-input" type="password" placeholder="Enter PIN"
            value={pin} onChange={e => setPin(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doPin()}
            maxLength={10} autoFocus />
          <button className="fp-btn fp-btn-primary" onClick={doPin} disabled={busy || !pin}>
            {busy ? 'Verifying…' : 'Confirm'}
          </button>
          {hasCred && (
            <button className="fp-btn fp-btn-secondary" onClick={() => setStep('scan')}>
              Use Fingerprint Instead
            </button>
          )}
        </>
      )
    },
  };

  const screen = SCREENS[step] || SCREENS.scan;

  return (
    <div className="fp-gate">
      <div className="fp-box">
        <div className="fp-icon">{screen.icon}</div>
        <div className="fp-title">{screen.title}</div>
        <div className="fp-sub">{screen.sub}</div>
        {error && <div className="fp-error">{error}</div>}
        {screen.actions}
      </div>
    </div>
  );
}

// ── LOCATION CARD ─────────────────────────────────────────
function LocationCard({ loc, isDraft, onActivate }) {
  return (
    <div className={`sal-card ${isDraft ? 'sal-card-draft' : 'sal-card-active'}`}>
      <div className="sal-card-header">
        <div className="sal-card-title-row">
          <span className="sal-card-name">{loc.name}</span>
          <span className={`sal-status-badge ${isDraft ? 'sal-draft' : 'sal-live'}`}>
            {isDraft ? '⏳ Pending' : '✓ Live'}
          </span>
        </div>
        <span className="sal-card-city">{loc.city}</span>
      </div>

      <div className="sal-card-details">
        <div className="sal-detail-row">
          <span className="sal-detail-lbl">GPS</span>
          <span className="sal-detail-val">{loc.gps_lat}, {loc.gps_lng}</span>
        </div>
        <div className="sal-detail-row">
          <span className="sal-detail-lbl">Geofence</span>
          <span className="sal-detail-val">{loc.geofence_radius}m</span>
        </div>
        <div className="sal-detail-row">
          <span className="sal-detail-lbl">Office IP</span>
          <span className={`sal-detail-val ${loc.allowed_ip ? 'sal-has-ip' : 'sal-no-ip'}`}>
            {loc.allowed_ip || '⚠ Not set'}
          </span>
        </div>
        <div className="sal-detail-row">
          <span className="sal-detail-lbl">WiFi BSSID</span>
          <span className="sal-detail-val">{loc.wifi_bssid || '—'}</span>
        </div>
        {loc.created_by_name && (
          <div className="sal-detail-row">
            <span className="sal-detail-lbl">Created by</span>
            <span className="sal-detail-val">{loc.created_by_name}</span>
          </div>
        )}
      </div>

      {isDraft && (
        <button className="sal-activate-btn" onClick={() => onActivate(loc)}>
          ✓ Activate Location
        </button>
      )}
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────
export default function SALocations() {
  const [verified, setVerified]     = useState(isSessionOk);
  const [locations, setLocations]   = useState([]);
  const [drafts, setDrafts]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [locked, setLocked]         = useState(false);
  const [fpEnabled, setFpEnabled]   = useState(isFpEnabled);
  const [showPinToggle, setShowPinToggle] = useState(false);
  const [toggleTarget, setToggleTarget]   = useState(null); // 'lock'|'fp'
  const [togglePin, setTogglePin]   = useState('');
  const [toggleErr, setToggleErr]   = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [locRes, draftRes, lockRes] = await Promise.all([
        fetch(`${BASE}/locations/`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BASE}/locations/drafts`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BASE}/settings/hr_location_lock`, { headers: authHeaders() }).then(r => r.json()),
      ]);
      setLocations(locRes.locations || []);
      setDrafts(draftRes.drafts || []);
      setLocked(lockRes.value === 'true');
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (verified) fetchData(); }, [verified, fetchData]);

  const activateLocation = async (loc) => {
    if (!window.confirm(`Activate "${loc.name}" in ${loc.city}?\nThis will make it live for employee clock-ins.`)) return;
    try {
      const res  = await fetch(`${BASE}/locations/${loc.id}/activate`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (res.ok) fetchData();
      else alert(data.error || 'Failed to activate');
    } catch { alert('Error'); }
  };

  const openToggle = (target) => {
    setToggleTarget(target);
    setTogglePin('');
    setToggleErr('');
    setShowPinToggle(true);
  };

  const confirmToggle = async () => {
    const entered = await sha256(togglePin);
    const correct = await sha256(FALLBACK_PIN);
    if (entered !== correct) { setToggleErr('Incorrect PIN'); return; }

    if (toggleTarget === 'lock') {
      const newLock = !locked;
      try {
        await fetch(`${BASE}/settings/hr_location_lock`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ value: String(newLock) }),
        });
        setLocked(newLock);
      } catch { setToggleErr('Failed to update'); return; }
    } else if (toggleTarget === 'fp') {
      const newState = !fpEnabled;
      localStorage.setItem(FP_EN_KEY, String(newState));
      setFpEnabled(newState);
    }

    setShowPinToggle(false);
    setTogglePin('');
  };

  if (!verified) return <FingerprintGate onVerified={() => setVerified(true)} />;

  const activeLocations = locations.filter(l => l.status === 'active' || !l.status);

  return (
    <div className="sa-section">

      {/* PIN confirm modal for toggle actions */}
      {showPinToggle && (
        <div className="fp-gate">
          <div className="fp-box">
            <div className="fp-icon">🔐</div>
            <div className="fp-title">
              {toggleTarget === 'lock'
                ? (locked ? 'Unlock Location Creation?' : 'Lock Location Creation?')
                : (fpEnabled ? 'Disable Biometric Lock?' : 'Enable Biometric Lock?')}
            </div>
            <div className="fp-sub">Enter your PIN to confirm this change</div>
            {toggleErr && <div className="fp-error">{toggleErr}</div>}
            <input className="fp-pin-input" type="password" placeholder="Enter PIN"
              value={togglePin} onChange={e => setTogglePin(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmToggle()} autoFocus />
            <button className="fp-btn fp-btn-primary" onClick={confirmToggle}>Confirm</button>
            <button className="fp-btn fp-btn-secondary" onClick={() => setShowPinToggle(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sal-header">
        <div>
          <h2 className="sa-section-title">Location Management</h2>
          <p className="sal-subtitle">
            {activeLocations.length} active · {drafts.length} pending activation
          </p>
        </div>
        <div className="sal-header-actions">
          <button
            className={`sal-fp-toggle ${locked ? 'sal-fp-locked' : 'sal-fp-unlocked'}`}
            onClick={() => openToggle('lock')}
            title="Toggle HR location creation lock"
          >
            {locked ? '🔒 HR Locked' : '🔓 HR Unlocked'}
          </button>
          <button
            className={`sal-fp-toggle ${fpEnabled ? 'sal-fp-on' : 'sal-fp-off'}`}
            onClick={() => openToggle('fp')}
            title="Toggle fingerprint requirement"
          >
            {fpEnabled ? '👆 Biometric ON' : '👆 Biometric OFF'}
          </button>
          <button
            className="sal-fp-toggle sal-fp-off"
            onClick={() => {
              if (window.confirm('Re-register your fingerprint?')) {
                localStorage.removeItem(CRED_KEY);
                setVerified(false);
                sessionStorage.removeItem(SESSION_KEY);
              }
            }}
          >
            ✋ Re-register FP
          </button>
        </div>
      </div>

      {/* Lock status banner */}
      {locked && (
        <div className="sal-lock-banner">
          🔒 Location creation is currently <strong>locked</strong> for HR.
          Click "HR Locked" to unlock after verification.
        </div>
      )}

      {/* Pending drafts */}
      {drafts.length > 0 && (
        <>
          <div className="sal-section-label sal-label-draft">
            ⏳ Pending Activation ({drafts.length})
          </div>
          <div className="sal-grid">
            {drafts.map((loc, i) => (
              <LocationCard key={i} loc={loc} isDraft onActivate={activateLocation} />
            ))}
          </div>
        </>
      )}

      {drafts.length === 0 && (
        <div className="sal-no-drafts">✓ No pending locations — all clear</div>
      )}

      {/* Active locations */}
      <div className="sal-section-label sal-label-active">
        Live Locations ({activeLocations.length})
      </div>

      {loading ? (
        <div className="sa-loading">Loading…</div>
      ) : (
        <div className="sal-grid">
          {activeLocations.map((loc, i) => (
            <LocationCard key={i} loc={loc} isDraft={false} onActivate={activateLocation} />
          ))}
        </div>
      )}
    </div>
  );
}